import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Link, useParams } from "react-router-dom";
import { CoreButton } from "../../core/ui/button/button";
import { CoreText, CoreTextType } from "../../core/ui/text/text";
import { SessionControls } from "../authorization/session_controls";
import { ReportStore } from "./report_store";
import "../task/task.css";
import "./reports.css";

export const ReportScreen = observer(
  ({ kind }: { kind: "exams" | "students" }) => {
    const { id } = useParams();
    const [store] = useState(() => new ReportStore());
    useEffect(() => {
      if (id) store.load(kind, id);
    }, [store, kind, id]);
    const report = store.report;
    const statistics = report?.statistics;
    const percent = statistics?.maxScore
      ? Math.round((statistics.score / statistics.maxScore) * 100)
      : null;
    return (
      <main className="task-page">
        <SessionControls />
        <Link to={kind === "exams" ? "/exam" : "/student"}>
          ← {kind === "exams" ? "Тесты и варианты" : "Ученики"}
        </Link>
        <header className="task-header">
          <CoreText
            as="h1"
            type={CoreTextType.big}
            text={report?.title || "Результаты"}
          />
          <CoreButton
            disabled={store.busy}
            onClick={() => id && store.load(kind, id, store.page)}
          >
            Обновить
          </CoreButton>
        </header>
        {store.busy && <p role="status">Загрузка результатов…</p>}
        {store.error && (
          <p role="alert" className="error-banner">
            {store.error}
          </p>
        )}
        {report && (
          <>
            <section className="report-metrics" aria-label="Общая статистика">
              <article className="panel">
                <p>Завершено попыток</p>
                <strong>
                  {statistics ? statistics.completed : report.completed}
                </strong>
              </article>
              <article className="panel">
                <p>{statistics ? "Пройдено заданий" : "Ученики"}</p>
                <strong>
                  {statistics ? statistics.solvedTasks : report.students}
                </strong>
              </article>
              <article className="panel">
                <p>{statistics ? "Общая успеваемость" : "Средний результат"}</p>
                <strong>
                  {(statistics ? percent : report.average) == null
                    ? "—"
                    : `${statistics ? percent : report.average}%`}
                </strong>
              </article>
              {!statistics && (
                <article className="panel">
                  <p>В процессе</p>
                  <strong>
                    {(report.total || 0) - (report.completed || 0)}
                  </strong>
                </article>
              )}
            </section>
            <section className="panel report-results">
              <CoreText as="h2" type={CoreTextType.header} text="Попытки" />
              {!report.attempts.length && (
                <p>
                  {store.page
                    ? "На этой странице нет попыток."
                    : "Попыток пока нет."}
                </p>
              )}
              <div className="report-attempts">
                {report.attempts.map((attempt) => (
                  <article className="report-attempt" key={attempt.id}>
                    <time>
                      {new Date(
                        attempt.completedAt || attempt.startedAt,
                      ).toLocaleString("ru-RU", { timeZone: "Europe/Moscow" })}
                    </time>
                    <div>
                      {kind === "exams" ? (
                        <Link to={`/student/${attempt.userId}/results`}>
                          {attempt.username}
                        </Link>
                      ) : (
                        <strong>{attempt.title}</strong>
                      )}
                    </div>
                    <p>
                      {attempt.status === "completed"
                        ? `${attempt.score} / ${attempt.maxScore}`
                        : "В процессе"}
                    </p>
                    <strong className="report-percent">
                      {attempt.percent == null ? "—" : `${attempt.percent}%`}
                    </strong>
                  </article>
                ))}
              </div>
              {(store.page > 0 || report.hasNext) && (
                <div className="row-actions report-pagination">
                  <CoreButton
                    disabled={store.busy || store.page === 0}
                    onClick={() => id && store.load(kind, id, store.page - 1)}
                  >
                    Назад
                  </CoreButton>
                  <span>Страница {store.page + 1}</span>
                  <CoreButton
                    disabled={store.busy || !report.hasNext}
                    onClick={() => id && store.load(kind, id, store.page + 1)}
                  >
                    Далее
                  </CoreButton>
                </div>
              )}
            </section>
            {statistics && (
              <section className="panel">
                <CoreText
                  as="h2"
                  type={CoreTextType.header}
                  text="Результаты по темам"
                />
                {!statistics.topics.length && (
                  <p>Завершённых тестов пока нет.</p>
                )}
                {statistics.topics.map((topic) => (
                  <article className="report-topic" key={topic.tag}>
                    <strong>{topic.tag}</strong>
                    <p>{topic.tasks} заданий</p>
                    <strong>
                      {topic.maxScore
                        ? `${Math.round((topic.score / topic.maxScore) * 100)}%`
                        : "—"}
                    </strong>
                  </article>
                ))}
              </section>
            )}
          </>
        )}
      </main>
    );
  },
);
